import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { Router } from '@angular/router';

interface Passenger {
  adult: number;
  child: number;
  infant: number;
}

interface ValidationErrors {
  adult?: string;
  child?: string;
  infant?: string;
}

@Component({
  selector: 'passenger-count-modal',
  templateUrl: './passenger-count-modal.component.html',
  styleUrls: ['./passenger-count-modal.component.scss'],
})
export class PassengerCountModalComponent implements OnInit, OnChanges {
  @Output() getPassengerData = new EventEmitter<any>();
  @Input() passengerData: any;
  @Input() fareType: string = '';
  passenger: Passenger = {
    adult: 1,
    child: 0,
    infant: 0,
  };
  travelClass = 'Economy';

  validationErrors: ValidationErrors = {};
  displayErrors: ValidationErrors = {};

  constructor(
    private modalController: ModalController,
    private translate: TranslateService,
    private analyticsService: AnalyticsService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializePassengerData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['passengerData'] && !changes['passengerData'].firstChange) {
      this.initializePassengerData();
    }
  }

  ionViewWillEnter() {
    this.initializePassengerData();
  }

  private initializePassengerData() {
    if (this.passengerData && Object.keys(this.passengerData).length !== 0) {
      this.passenger = {
        adult: this.passengerData.adult || 1,
        child: this.passengerData.child || 0,
        infant: this.passengerData.infant || 0,
      };
      this.travelClass = this.isEconomyOnlyMode() ? 'Economy' : (this.passengerData.travelClass || 'Economy');
    } else {
      this.passenger = {
        adult: 1,
        child: 0,
        infant: 0,
      };
      // For flexicancel, always set to Economy
      this.travelClass = this.isFlexiCancelMode() ? 'Economy' : 'Economy';
    }
    
    // Clear all errors when modal opens
    this.displayErrors = {};
    this.validationErrors = {};
    
    // Validate current state
    this.validatePassengerCounts();
    
    // Only show display errors if current state is invalid
    if (!this.isValidPassengerCount()) {
      this.displayErrors = { ...this.validationErrors };
    }
    
    this.emitPassengerData();
  }

  emitPassengerData() {
    if (this.isEconomyOnlyMode()) {
      this.travelClass = 'Economy';
    }
    this.getPassengerData.emit({
      ...this.passenger,
      travelClass: this.travelClass,
    });
  }

  add(type: string) {
    const previousPassenger = { ...this.passenger };
    
    switch (type) {
      case 'adult':
        this.passenger.adult = this.passenger.adult + 1;
        break;
      case 'child':
        this.passenger.child = this.passenger.child + 1;
        break;
      case 'infant':
        this.passenger.infant = this.passenger.infant + 1;
        break;
      default:
        return;
    }
    
    // Check if the new state has validation errors
    const tempValidationErrors = this.getValidationErrors(this.passenger);
    const isValidNewState = Object.keys(tempValidationErrors).length === 0;
    
    // Check if adding violates maximum limits (these should be hard restrictions)
    const shouldRevertAddition = this.shouldRevertAddition(this.passenger, type);
    
    if (shouldRevertAddition) {
      // Only revert if we're hitting hard limits (like max 9 adults)
      this.passenger = previousPassenger;
      this.displayErrors = tempValidationErrors;
      this.validationErrors = this.getValidationErrors(this.passenger);
    } else {
      // Allow the addition and update error state based on current validation
      this.validationErrors = tempValidationErrors;
      
      if (isValidNewState) {
        this.displayErrors = {};
        this.getPassengerData.emit({
          ...this.passenger,
          travelClass: this.travelClass,
        });
      } else {
        this.displayErrors = { ...this.validationErrors };
      }
    }
  }

  subtract(type: string) {
    switch (type) {
      case 'adult':
        this.passenger.adult = this.passenger.adult - 1;
        if (this.passenger.adult < 1) {
          this.passenger.adult = 1;
        }
        break;
      case 'child':
        this.passenger.child = this.passenger.child - 1;
        if (this.passenger.child < 0) {
          this.passenger.child = 0;
        }
        break;
      case 'infant':
        this.passenger.infant = this.passenger.infant - 1;
        if (this.passenger.infant < 0) {
          this.passenger.infant = 0;
        }
        break;
      default:
        return;
    }
    
    // Validate current state
    this.validationErrors = this.getValidationErrors(this.passenger);
    
    if (this.isValidPassengerCount()) {
      this.displayErrors = {};
      this.getPassengerData.emit({
        ...this.passenger,
        travelClass: this.travelClass,
      });
    } else {
      this.displayErrors = { ...this.validationErrors };
    }
  }

  getValidationErrors(passengerData: Passenger): ValidationErrors {
    const errors: ValidationErrors = {};

    // Adult validation: Minimum 1, Maximum 9
    if (passengerData.adult < 1) {
      errors.adult = this.translate.instant('flight.passenger_count_modal.minimum_one_adult_is_required');
    } else if (passengerData.adult > 9) {
      errors.adult = this.translate.instant('flight.passenger_count_modal.maximum_nine_adults_allowed');
    }

    // Child validation: Maximum 9
    if (passengerData.child > 9) {
      errors.child = this.translate.instant('flight.passenger_count_modal.maximum_nine_children_allowed');
    }

    // Total adults and children cannot exceed 9
    const totalAdultsAndChildren = passengerData.adult + passengerData.child;
    if (totalAdultsAndChildren > 9) {
      // Show error on both adult and child fields when total exceeds 9
      if (!errors.adult) {
        errors.adult = this.translate.instant('flight.passenger_count_modal.total_adults_and_children_cannot_exceed_9');
      }
      if (!errors.child) {
        errors.child = this.translate.instant('flight.passenger_count_modal.total_adults_and_children_cannot_exceed_9');
      }
    }

    // Infant validation: Maximum 4
    if (passengerData.infant > 4) {
      errors.infant = this.translate.instant('flight.passenger_count_modal.maximum_four_infants_allowed');
    }

    // Infants cannot travel alone - need at least one adult per infant
    if (passengerData.infant > passengerData.adult) {
      errors.infant = this.translate.instant('flight.passenger_count_modal.infants_cannot_travel_alone_adult_count_must_be_greater_than_or_equal_to_infant_count');
    }

    return errors;
  }

  validatePassengerCounts(): void {
    this.validationErrors = this.getValidationErrors(this.passenger);
  }

  isValidPassengerCount(): boolean {
    return Object.keys(this.validationErrors).length === 0;
  }

  isFlexiCancelMode(): boolean {
    const currentUrl = this.router.url || window.location.pathname;
    const isFlexiCancelRoute = currentUrl.includes('flexicancel');
    const isFlexiCancelFareType = this.fareType === 'flexicancel' || this.fareType === 'flexi_cancel';
    return isFlexiCancelRoute || isFlexiCancelFareType;
  }

  isJspMode(): boolean {
    const currentUrl = this.router.url || window.location.pathname;
    const isJspRoute = currentUrl.includes('jsp');
    const isJspFareType = this.fareType === 'jsp';
    return isJspRoute || isJspFareType;
  }

  isEconomyOnlyMode(): boolean {
    return this.isFlexiCancelMode() || this.isJspMode();
  }

  shouldRevertAddition(passengerData: Passenger, type: string): boolean {
    // Only revert additions that violate hard maximum limits
    // Don't revert additions that could fix dependency issues
    switch (type) {
      case 'adult':
        return passengerData.adult > 9 || (passengerData.adult + passengerData.child) > 9;
      case 'child':
        return passengerData.child > 9 || (passengerData.adult + passengerData.child) > 9;
      case 'infant':
        return passengerData.infant > 4;
      default:
        return false;
    }
  }

  // Function to Close Passenger Count Modal
  closeModal() {
    // Clear all errors when closing modal
    this.displayErrors = {};
    this.validationErrors = {};
    this.analyticsService.trackUnified('traveller_count_modal_closed', {
      flow: 'flight',
      outcome: 'success',
      legacy_event_name: 'button_clicked',
      button_name: 'Close Traveller Count Modal',
    });
    this.analyticsService.trackUnified('traveller_count_details', {
      flow: 'flight',
      outcome: 'success',
      legacy_event_name: 'Traveller Count Details',
      traveller_count: this.passenger,
      travel_class: this.travelClass,
    }); 
    this.modalController.dismiss();
  }

  // Method to format passenger counts as a string
  getPassengerCountString(): string {
    // eslint-disable-next-line max-len
    return `${this.passenger.adult} ${this.translate.instant(
      'flight.passenger_count_modal.adult'
    )} , ${this.passenger.child} ${this.translate.instant(
      'flight.passenger_count_modal.child'
    )}, ${this.passenger.infant} ${this.translate.instant(
      'flight.passenger_count_modal.infant'
    )}`;
  }
}
